import { push } from "connected-react-router";
import { Field, Form, Formik } from "formik";
import useSchema from "hooks/useSchema";
import { isEmpty } from "lodash";
import moment from "moment";
import Scheduler from "pages/forms/_components/_fields/Scheduler";
import Button from "pages/_components/Button";
import AmountField from "pages/_components/fields/formik/AmountField";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import SwitchField from "pages/_components/fields/formik/SwitchField";
import TextField from "pages/_components/fields/TextField";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { actions as accountsActions } from "reducers/accounts";
import { actions as notificationActions } from "reducers/notification";
import { actions as transferActions } from "reducers/transfers";
import gaEventTracker, { events } from "util/gaEventTracker";
import * as i18n from "util/i18n";
import * as schedulerUtils from "util/scheduler";
import * as Yup from "yup";
import { bool } from "prop-types";
import { useTransferAccounts } from "../hooks/useTransferAccounts";
import useInternFieldChange from "../hooks/useInternFieldChange";
import { useAccount } from "hooks/useAccounts";

const FORM_ID = "transfer";

const InternalTransferPage = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const { stringRequired, numberRequired, emailRequired } = useSchema();
    const { lang } = useSelector((state) => state.i18n);
    const { prevRoute } = useSelector((state) => state.form);
    const { selectedAccount = {} } = useSelector((state) => state.accounts);
    const transfers = useSelector((state) => state.transfers);
    const transferData = transfers.internalTransferData;
    const [persistValues, setPersistValues] = useState({});
    const { state } = useLocation();
    const formRef = useRef(null);
    const { debitAccountOptions, creditAccountOptions, handleCreditOptions, fetching, accounts } = useTransferAccounts(
        "internalTransfers",
    );
    const { handleOnChangeAmount, handleChangeEmail, handleChangeMessage } = useInternFieldChange();

    const { programTrasferData } = useSelector((sta) => sta.transfers);
    const currencies = [
        { id: "ARS", label: i18n.get(`currency.label.ARS`) },
        { id: "USD", label: i18n.get(`currency.label.USD`) },
    ];

    const debitAccountOptionsFiltered = debitAccountOptions.filter((obj) => !obj.label.includes("CA U$D"));

    const [prefixValue, setPrefixValue] = useState();

    const initialValues = {
        debitAccount: transferData?.debitAccount || state?.accountId || "",
        creditAccount: transferData?.creditAccount || "",
        amountField: {
            amount: transferData?.amountField?.amount || null,
            currency: transferData?.amountField?.currency || state?.currency || "ARS",
        },
        sendEmail: transferData?.sendEmail || false,
        email: transferData?.email || "",

        messageText: transferData?.emailText || "",
        scheduler: programTrasferData || null,
        reasonCode: transferData?.emailText || "VAR",
        reference: transferData?.reference || "",
        reason: transferData?.reason || "",
        ...persistValues,
    };

    const data = {
        enabledWeekDays: [1, 2, 3, 4, 5, 6, 7],
        firstWorkingDate: moment(),
        maxDaysToSchedule: 10000,
        nonWorkingDays: [],
        lang,
        mode: "edit",
        programable: true,
        schedulable: true,
    };

    const { account: _debitAccount, formatAccountBody: formatDebitAccountBody } = useAccount(
        "productId",
        transferData.debitAccount,
    );
    const { account: _creditAccount, formatAccountBody: formatCreditAccountBody } = useAccount(
        "productId",
        transferData.creditAccount,
    );

    const internalTransferSchema = Yup.object().shape({
        debitAccount: stringRequired,
        creditAccount: stringRequired,
        amountField: Yup.object()
            .shape({
                amount: numberRequired,
            })
            .test(
                "availableAmount",
                i18n.get("transfer.amount.error"), // eslint-disable-next-line func-names
                function(value) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    const { debitAccount = "" } = this.parent;
                    const selected = accounts.find((_account) => _account.productId === debitAccount);
                    if (value.amount > selected?.availableBalance) {
                        dispatch(
                            notificationActions.showNotification("transfers.insuficient.amount.error", "error", [
                                "internalTransfers",
                            ]),
                        );
                    }
                    return value.amount <= selected?.availableBalance;
                },
            ),
        email: Yup.string().when("sendEmail", (sendEmail, schema) => (sendEmail ? emailRequired : schema)),
    });

    const handleSubmit = (values) => {
        const value = {
            ...values,
            debitAccount: values.debitAccount,
            debitAccountLabel: formatDebitAccountBody(_debitAccount),
            creditAccountCbuNumber: selectedAccount.uniformBankCode,
            creditAccountLabel: formatCreditAccountBody(_creditAccount),
            reasonCode: values.reasonCode,
            amount: {
                quantity: values.amountField.amount,
                currency: values.amountField.currency,
            },
            emailText: values.messageText,
            cuitRecipient: selectedAccount.taxBankCode,
            nameRecipient: selectedAccount.ownerName,
            emailRecipient: values.email,
            scheduler:
                values.scheduler.selectedOption !== schedulerUtils.TODAY
                    ? values.scheduler || programTrasferData
                    : null,
        };
        dispatch(transferActions.setInternalTransferData(value));
        dispatch(push("/transfers/internal/confirm"));
    };

    const handleDebitAccount = (debitAccountId, values, setFieldValue = null) => {
        const selected = accounts.find((_account) => _account.productId === debitAccountId);
        setPrefixValue(
            selected?.currency === "ARS" ? i18n.get(`currency.label.ws.ARS`) : i18n.get(`currency.label.ws.USD`),
        );
        const isDifferentCurrency = selected?.currency !== values?.amountField?.currency;
        const isSameAccount = values?.creditAccount === debitAccountId;
        handleCreditOptions(selected?.currency, debitAccountId);
        if (setFieldValue) {
            setFieldValue("amountField.currency", selected?.currency || values?.amountField?.currency);
        }

        if (isSameAccount || isDifferentCurrency) {
            if (setFieldValue) {
                setFieldValue("creditAccount", "");
            }
        }
        dispatch(
            transferActions.setInternalTransferData({
                ...values,
                amountField: { ...values?.amountField, currency: selected?.currency || values?.amountField?.currency },
                debitAccount: selected?.productId,
            }),
        );
    };

    const handleCreditAccount = (creditAccountId, values) => {
        const selected = accounts.find((_account) => _account?.productId === creditAccountId);
        setPersistValues({ ...values, creditAccount: creditAccountId });
        dispatch(accountsActions.readAccount(creditAccountId));
        dispatch(
            transferActions.setInternalTransferData({
                ...values,
                creditAccount: selected?.productId,
            }),
        );
    };

    useEffect(() => {
        if (!isEmpty(transferData)) {
            handleDebitAccount(transferData?.debitAccount, transferData);
        }
        if (state?.accountId) {
            handleDebitAccount(state?.accountId);
        }
    }, []);

    useEffect(() => {
        if (!formRef.current || state?.accountId) {
            return;
        }

        const { prevPath = "" } = prevRoute;
        const fromOtherPage = !prevPath.split("/").includes("transfers");
        if (fromOtherPage) {
            formRef.current.resetForm({
                debitAccount: "",
                creditAccount: "",
                amountField: {
                    amount: "",
                    currency: "ARS",
                },
                sendEmail: false,
                email: "",
                messageText: "",
                scheduler: null,
                reasonCode: "VAR",
                reference: "",
                reason: "",
            });
        }
    }, []);

    const programTransfer = (value) => {
        gaEventTracker.triggerEvent(events.programar({ frecuencia: value?.frequency }));
    };

    return (
        <>
            <Notification scopeToShow="internalTransfers" />
            <PageLoading loading={fetching}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={internalTransferSchema}
                    ref={formRef}>
                    {({ values, setFieldValue }) => {
                        const canSubmit =
                            !values.debitAccount ||
                            !values.creditAccount ||
                            !values.amountField.amount ||
                            (!values.email && values.sendEmail);

                        return (
                            <Form>
                                <GridTable className="form">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data className="required" columnStart={2} columnWidth={10}>
                                            <Field
                                                component={ProductSelector}
                                                label={i18n.get(`${FORM_ID}.debitAccount.label`)}
                                                name="debitAccount"
                                                placeholder={i18n.get("global.select.account.placeholder")}
                                                data={{ emptyOptionLabel: "", options: debitAccountOptionsFiltered }}
                                                onChange={(id) => handleDebitAccount(id, values, setFieldValue)}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data className="required" columnStart={2} columnWidth={10}>
                                            <Field
                                                component={ProductSelector}
                                                label={i18n.get(`${FORM_ID}.creditAccount.label`)}
                                                name="creditAccount"
                                                disabled={!values.debitAccount}
                                                placeholder={i18n.get("global.select.account.placeholder")}
                                                data={{ emptyOptionLabel: "", options: creditAccountOptions }}
                                                onChange={(id) => handleCreditAccount(id, values)}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data className="required" columnStart={2} columnWidth={5}>
                                            <Field
                                                component={AmountField}
                                                labelText={i18n.get(`${FORM_ID}.amountField.label`)}
                                                name="amountField"
                                                disableNumber={!values.debitAccount}
                                                data={{ options: currencies }}
                                                clearable={false}
                                                integerLength={10}
                                                decimalLength={2}
                                                onBlurChange={(amount) =>
                                                    handleOnChangeAmount(amount, values, setFieldValue)
                                                }
                                                withValueCap
                                                disableSelect
                                                fixedDecimalScale
                                                hideCurrency
                                                prefix={prefixValue}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={5}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="reference"
                                                type="text"
                                                hidePlaceholder
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={5}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="reason"
                                                type="text"
                                                hidePlaceholder
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            className={!isDesktop && "btn-full-width"}
                                            columnStart={2}
                                            columnWidth={10}
                                            flexDirection="column">
                                            <Field
                                                component={Scheduler}
                                                data={data}
                                                name="scheduler"
                                                showLabel={false}
                                                persisData
                                                dispatch={dispatch}
                                                programTrasferData={programTrasferData}
                                                showAsSidebar
                                                handleChange={programTransfer}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={2}
                                            columnWidth={10}
                                            alignY="flex-start"
                                            className="h-full required">
                                            <Field
                                                component={SwitchField}
                                                idForm={FORM_ID}
                                                name="sendEmail"
                                                onChange={() => {
                                                    dispatch(
                                                        transferActions.setInternalTransferData({
                                                            ...values,
                                                            sendEmail: !values.sendEmail,
                                                        }),
                                                    );
                                                    if (values.sendEmail) {
                                                        setFieldValue("email", "");
                                                        setFieldValue("messageText", "");
                                                    }
                                                }}
                                            />
                                        </GridTable.Data>
                                        {values.sendEmail && (
                                            <>
                                                <GridTable.Data columnStart={2} columnWidth={5}>
                                                    <Field
                                                        component={TextField}
                                                        labelnoI18n={i18n.get("global.email.label")}
                                                        idForm={FORM_ID}
                                                        name="email"
                                                        type="email"
                                                        placeholder={i18n.get("global.email.placeholder")}
                                                        handleOnChange={(email) => handleChangeEmail(email, values)}
                                                    />
                                                </GridTable.Data>
                                                <GridTable.Data columnStart={7} columnWidth={5}>
                                                    <Field
                                                        component={TextField}
                                                        idForm={FORM_ID}
                                                        name="messageText"
                                                        maxLength="500"
                                                        type="text"
                                                        placeholder={i18n.get("global.message.placeholder")}
                                                        handleOnChange={(message) =>
                                                            handleChangeMessage(message, values)
                                                        }
                                                    />
                                                </GridTable.Data>
                                            </>
                                        )}
                                    </GridTable.Container>
                                </GridTable>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            disabled={canSubmit}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </PageLoading>
        </>
    );
};

InternalTransferPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default InternalTransferPage;
